<template>
	<div class="app-manager" :class="classList">
		<navbar />
		<sidebar />
		<main class="app-view-container">
			<router-view class="app-view" />
		</main>
		<alerts-panel
			:not-to-continue-with-audio-alert="notToContinueWithAudioAlert"
			:show="visible"
			@close="onAlertsPanelClose"
		/>
		<general-popup
			:show="localAudioEngagementPopupOpen"
			message="Continue using audio alerts?"
			@yes="onAudioEngagementGeneralYes"
			@no="onAudioEngagementGeneralNo"
		/>
	</div>
</template>
<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import Navbar from '@/components/Navbar.vue'
import Sidebar from '@/components/Sidebar.vue'
import Bugsnag from '@bugsnag/js'
import _get from 'lodash/get'
import isEqual from 'lodash/isEqual'
import {
	UPDATE_CALL_COUNT_DURING_INACTIVITY,
	UPDATE_USER_ACTIVE_ON_SCREEN,
} from '@/store/mutation-types'
import AlertsPanel from '@/components/Panels/AlertsPanel.vue'
import GeneralPopup from '@/components/Panels/GeneralPopup.vue'

export default {
	/**
	 * The component's registered child components.
	 *
	 * @type {Object}
	 */
	components: {
		Navbar,
		Sidebar,
		AlertsPanel,
		GeneralPopup,
	},

	/**
	 * The component's computed properties.
	 *
	 * @type {Object}
	 */
	computed: {
		/**
		 * Get the current active partner id from the store.
		 *
		 * @return {?Number}
		 */
		activePartnerId() {
			return this.$store.getters.partner?.id
		},

		/**
		 * Get the wrapper CSS class list.
		 *
		 * @return {Object}
		 */
		classList() {
			return {
				'has-inbox': this.$route.meta.inbox,
				'has-narrow-inbox': this.$route.meta.narrow,
			}
		},

		...mapState({
			userActiveOnScreen: state => state.calls.userActiveOnScreen,
			callCountDuringInactivity: state =>
				state.calls.callCountDuringInactivity,
			visible: state => state.settings.alertSettingsPopup.visible,
			alertPreferences: state => state.settings.alertPreferences,
			appUserInteractionReceived: state =>
				state.settings.appUserInteractionReceived,
			user: state => state.auth.user,
			session: state => state.auth.session,
		}),
	},

	/**
	 * The component's local methods.
	 *
	 * @type {Object}
	 */
	methods: {
		async logCallEvent(data, type) {
			const stateMap = _get(this.$route, 'meta.stateMap', null)
			let state
			if (!stateMap) {
				state = this.$route.name
			} else {
				state = this.$store?.state[stateMap].filters || stateMap
			}

			const appRouteDetails = {
				route: this.$route.path,
				routeState: state,
			}

			this.$api.eventLogger().create({
				log_only: true,
				summary: `${type} Call event received for Partner: ${data.partner_id} of CallID: ${data.sid} `,
				metadata: JSON.stringify({
					timestamp: new Date().toISOString(),
					localTime: new Date().toLocaleString(),
					utcTime: new Date().toUTCString(),
					online: window.navigator.onLine,
					networkSpeed: window.navigator.connection
						? window.navigator.connection.effectiveType
						: 'unknown',
					appRouteDetails: JSON.stringify(appRouteDetails),
					visibilityState: document.visibilityState,
				}),
			})
		},

		/**
		 * Fetch the new call from the API when the event is received.
		 *
		 * @param {String} options.call_sid
		 * @return {void}
		 */
		async onNewCall({ data }) {
			const enableLog = this.$store?.getters?.partner?.track_events
			enableLog && (await this.logCallEvent(data, 'New'))

			if (data.partner_id !== this.activePartnerId) {
				Bugsnag.notify(
					new Error(
						'Received new call event for partner ' +
							data.partner_id +
							' but active partner is ' +
							this.activePartnerId
					)
				)
				return
			}
			try {
				// Ignore call if user is limited to view own pages only
				// and the call is not owned by the user
				const user = this.$store?.state?.auth?.user
				const viewOwnPagesOnly = user?.view_own_pages_only
				if (
					this.partnerHasPagingEnabled() &&
					viewOwnPagesOnly &&
					data?.source === 'page'
				) {
					const isOwnCall =
						user?.id != null && user.id === data.created_by
					if (!isOwnCall) {
						return
					}
					data.call_count_stats = data.user_call_count_stats
				}

				if (this.startDateFilter() && this.endDateFilter()) {
					await this.getIncompleteCallCount()
					await this.getIncompletePagingCount()

					if (data && data.types && data.types.length > 0) {
						await data.types.map(type => {
							this.getCallByType(type.id)
						})
					}
					if (data.call_count_stats) {
						this.setIncompleteTotalCountOnly({
							total: data.call_count_stats.total,
						})
					}
				} else {
					if (data.call_count_stats) {
						await this.setIncompleteCallCount({
							urgent: data.call_count_stats.urgent,
							non_urgent: data.call_count_stats.non_urgent,
							total: data.call_count_stats.total,
							setTotal: true,
						})
						await this.incrementPendingCallsCountById({
							total: data.call_count_stats.total,
						})
						if (
							data.call_count_stats.call_types &&
							data.call_count_stats.call_types.length > 0
						) {
							await data.call_count_stats.call_types.map(type => {
								if (type.id && Number.isInteger(type.count)) {
									this.updateCallTypeCount({
										id: type.id,
										total: type.count,
									})
								}
							})
						}
					}
				}

				await this.addRecentCall(data.id)
				await this.addToFront(data)
				await this.addToFilter(data)
			} catch (e) {
				console.error(`Failed to fetch new call [sid: ${data.sid}`)
			}
		},

		/**
		 * Fetch the update call from the API when the event is received.
		 *
		 * @param {String} options.call_sid
		 * @return {void}
		 */
		async onUpdateCall({ data }) {
			const enableLog = this.$store?.getters?.partner?.track_events
			enableLog && (await this.logCallEvent(data, 'Update'))

			if (data.partner_id !== this.activePartnerId) {
				Bugsnag.notify(
					new Error(
						'Received update call event for partner ' +
							data.partner_id +
							' but active partner is ' +
							this.activePartnerId
					)
				)
				return
			}
			try {
				// Ignore call if user is limited to view own pages only
				// and the call is not owned by the user
				const user = this.$store?.state?.auth?.user
				const viewOwnPagesOnly = user?.view_own_pages_only
				if (
					this.partnerHasPagingEnabled() &&
					viewOwnPagesOnly &&
					data?.source === 'page'
				) {
					const isOwnCall =
						user?.id != null && user.id === data.created_by
					if (!isOwnCall) {
						return
					}
					data.call_count_stats = data.user_call_count_stats
				}

				if (this.startDateFilter() && this.endDateFilter()) {
					await this.getIncompleteCallCount()
					await this.getIncompletePagingCount()
					await this.getCallTypeCounts()
				} else {
					if (data.call_count_stats) {
						await this.setIncompleteCallCount({
							urgent: data.call_count_stats.urgent,
							non_urgent: data.call_count_stats.non_urgent,
							total: data.call_count_stats.total,
							setTotal: true,
						})
						await this.incrementPendingCallsCountById({
							total: data.call_count_stats.total,
						})
						if (
							data.call_count_stats.call_types &&
							data.call_count_stats.call_types.length > 0
						) {
							await data.call_count_stats.call_types.map(type => {
								if (type.id && Number.isInteger(type.count)) {
									this.updateCallTypeCount({
										id: type.id,
										total: type.count,
									})
								}
							})
						}
					}
				}

				if (data.sid === this.$route.params.sid) {
					const response = await this.$api
						.partners(this.activePartnerId)
						.calls()
						.find(data.sid)
					const call = response.get('data', {})
					await this.addToFront(call)
				} else {
					await this.addToFront(data)
				}
				await this.addRecentCall(data.id)
				await this.updateToFilter(data)
			} catch (e) {
				console.error(`Failed to fetch new call [sid: ${data.sid}`)
			}
		},

		/**
		 * Handle the partner migrated event.
		 *
		 * @param {Object} event
		 * @return {void}
		 */
		async onPartnerMigrated(event) {
			if (event?.source_partner_id !== this.activePartnerId) {
				return Bugsnag.notify(
					new Error(
						'Received partner migrated event for partner ' +
							event.source_partner_id +
							' but active partner is ' +
							this.activePartnerId
					)
				)
			}

			const targetPartnerId = event?.target_partner_id
			if (
				typeof targetPartnerId !== 'number' ||
				!Number.isFinite(targetPartnerId)
			) {
				return Bugsnag.notify(
					new Error(
						'Received partner migrated event with invalid target partner id: ' +
							targetPartnerId
					)
				)
			}
			await this.setActivePartner(targetPartnerId)
		},

		/**
		 * Handle the OMD new release event.
		 *
		 * @param {Object} event
		 * @return {void}
		 */
		onOmdRelease(event) {
			if (event?.force_refresh) {
				this.$alert.warning('omd.release')

				setTimeout(() => location.reload(), 5000)
			}
		},

		/**
		 * Redirect the app to the given partner id.
		 *
		 * @param {Number} id
		 * @return {void}
		 */
		redirectToPartner(id) {
			const name = this.$route.name
			const params = this.$route.params

			this.$router.push({
				name,
				params: {
					...params,
					partner: id,
				},
			})
		},

		/**
		 * Determine if the app should redirect the user to the given partner id.
		 *
		 * @param {Number} id
		 * @return {Boolean}
		 */
		shouldRedirectToPartner(id) {
			const current = parseInt(this.$route.params.partner)

			return id && id !== current
		},

		handleWindowActivity(eventType) {
			switch (eventType) {
				case 'click':
					if (!this.userActiveOnScreen) {
						this.updateUserActiveOnScreen(true)
					}
					break
				case 'visibilitychange':
					this.updateUserActiveOnScreen(
						document.visibilityState === 'visible'
					)
					break
				case 'mousemove': {
					let lastLogTime = 0
					const now = Date.now()
					// To avoid excessive tracking,
					// Only log mouse movement once every 5 seconds.
					if (now - lastLogTime > 5 * 1000) {
						lastLogTime = now
						clearTimeout(this.mouseNotMoveTimeout)
						this.mouseNotMoveTimeout = setTimeout(() => {
							this.updateUserActiveOnScreen(false)
						}, this.alertPreferences.inactivity_threshold_minutes * 60 * 1000) // n minute of no mouse movement
					}
					break
				}
			}
		},

		trackInactivity(toggle = true) {
			if (!toggle) {
				window.removeEventListener('click', this.boundClickHandler)
				window.removeEventListener(
					'visibilitychange',
					this.boundVisibilityHandler
				)
				window.removeEventListener('mousemove', this.boundMouseHandler)
				return
			}

			window.addEventListener('click', this.boundClickHandler)
			window.addEventListener(
				'visibilitychange',
				this.boundVisibilityHandler
			)
			window.addEventListener('mousemove', this.boundMouseHandler)
		},

		clearAudioEngagementTimer() {
			if (this.audioEngagementTimerId) {
				clearTimeout(this.audioEngagementTimerId)
				this.audioEngagementTimerId = null
			}
		},

		onAlertsPanelClose() {
			this.notToContinueWithAudioAlert = false
			this.getAlertPopStatus()
		},

		onAudioEngagementGeneralNo() {
			this.localAudioEngagementPopupOpen = false
			this.clearAudioEngagementTimer()
			this.notToContinueWithAudioAlert = true
			this.getAlertPopStatus()
		},

		onAudioEngagementGeneralYes() {
			this.localAudioEngagementPopupOpen = false
			this.clearAudioEngagementTimer()
			this.tryPrimeAudioAlertFromUserGesture()
		},

		/**
		 * Browsers block playback until there has been a user gesture.
		 * Prime once (e.g. after window click or audio engagement confirmation) so later alerts can play.
		 */
		tryPrimeAudioAlertFromUserGesture() {
			if (this.audioAutoplayPrimed || this._audioPrimeInProgress) return
			const { alert_sound, audio_alert_enabled } =
				this.alertPreferences || {}
			if (!audio_alert_enabled || !alert_sound) return

			this._audioPrimeInProgress = true
			const audio = new Audio(alert_sound)
			audio.volume = 0.01
			audio
				.play()
				.then(() => {
					try {
						audio.pause()
						audio.currentTime = 0
					} catch (_) {
						/* ignore */
					}
					audio.volume = 1
					this._reusableAlertAudio = audio
					this._primedAlertSoundUrl = alert_sound
					this.audioAutoplayPrimed = true
					this._audioPrimeInProgress = false
				})
				.catch(() => {
					this._audioPrimeInProgress = false
				})
		},

		updateIntercom(newValue, oldValue) {
			const ic = window.Intercom
			if (!newValue) {
				ic?.('shutdown')
				return
			}
			if (oldValue != null && isEqual(newValue?.id, oldValue?.id)) return
			if (!newValue.id) return
			const roles = []
			for (const key in newValue.roles || {}) {
				const r = newValue.roles[key]
				if (
					r.is_partner_role &&
					(r.name === 'office_manager' || r.name === 'partner-admin')
				) {
					!roles.includes('manager') && roles.push('manager')
				}
			}
			for (const key in newValue.providers || {}) {
				const p = newValue.providers[key]
				if (p.active && p.user_id === newValue.id) {
					!roles.includes('provider') && roles.push('provider')
				}
			}
			if (!ic) return
			const partners = newValue.partners || []
			const mapPartnerToCompany = partner => ({
				company_id: partner.id,
				name: partner.name,
				industry: 'Healthcare',
				custom_attributes: partner?.intercom_attributes || {},
			})
			const company = partners.find(partner => {
				return (
					String(partner.id) === String(newValue.primary_partner_id)
				)
			})
			ic('shutdown')
			ic('boot', {
				api_base:
					process.env.VUE_APP_INTERCOM_API ||
					'https://api-iam.intercom.io',
				app_id: process.env.VUE_APP_INTERCOM_ID || 'z1oijek0',
				user_id: newValue.id,
				name: `${newValue.first_name} ${newValue.last_name}`,
				email: newValue.email,
				clarus_role: roles.toString(),
				user_hash: newValue.intercom_hash_web,
				...(company && {
					custom_attributes: {
						primary_company_id: company.id,
						primary_company_name: company.name,
						primary_company_role: newValue.primary_partner_role,
					},
				}),
				companies: partners.map(mapPartnerToCompany),
			})
		},

		pingVisualAndAudioAlert({ newCallsCount }) {
			const {
				audio_alert_enabled,
				visual_alert_enabled,
				alert_sound,
				alert_color,
			} = this.alertPreferences

			// Constants
			const FAVICON_CONFIG = {
				BASE_IMAGE_URL:
					'https://upload.wikimedia.org/wikipedia/commons/7/70/User_icon_BLACK-01.png',
				SIZE: 24,
				BADGE_RADIUS: 10,
				BADGE_POSITION: { x: 14, y: 10 },
				DEFAULT_COLOR: 'red',
				FONT: 'bold 14px Arial',
				MAX_DISPLAY_COUNT: 9,
			}

			// Helper: Update favicon in document head
			const setFavicon = href => {
				// Remove existing favicon links
				document
					.querySelectorAll("link[rel='icon']")
					.forEach(element => element.remove())

				// Create and append new favicon link
				const faviconLink = document.createElement('link')
				faviconLink.id = 'dynamic-favicon'
				faviconLink.rel = 'icon'
				faviconLink.type = 'image/png'
				faviconLink.href = href
				document.head.appendChild(faviconLink)
			}

			// Helper: Restore default favicon when no new calls
			const restoreDefaultFavicon = () => {
				const defaultFaviconURL = `${process.env.BASE_URL}favicon.png`
				setFavicon(defaultFaviconURL)
			}

			// Helper: Format call count for display
			const formatCallCount = count => {
				return count > FAVICON_CONFIG.MAX_DISPLAY_COUNT
					? `${FAVICON_CONFIG.MAX_DISPLAY_COUNT}+`
					: count.toString()
			}

			// Helper: Draw badge with call count on canvas
			const drawBadgeOnCanvas = (context, callCount, color) => {
				const { BADGE_RADIUS, BADGE_POSITION, FONT } = FAVICON_CONFIG

				// Draw badge circle
				context.beginPath()
				context.arc(
					BADGE_POSITION.x,
					BADGE_POSITION.y,
					BADGE_RADIUS,
					0,
					2 * Math.PI
				)
				context.fillStyle = color || FAVICON_CONFIG.DEFAULT_COLOR
				context.fill()

				// Draw call count text
				context.fillStyle = 'white'
				context.font = FONT
				context.textAlign = 'center'
				context.textBaseline = 'middle'
				context.fillText(
					formatCallCount(callCount),
					BADGE_POSITION.x,
					BADGE_POSITION.y
				)
			}

			// Helper: Create canvas with badge
			const createBadgeCanvas = (baseImageUrl, callCount, color) => {
				return new Promise((resolve, reject) => {
					const { SIZE } = FAVICON_CONFIG
					const canvas = document.createElement('canvas')
					canvas.width = SIZE
					canvas.height = SIZE
					const context = canvas.getContext('2d')

					const image = new Image()
					image.crossOrigin = 'anonymous'
					image.src = baseImageUrl

					image.onload = () => {
						// Draw base image
						context.drawImage(image, 0, 0, SIZE, SIZE)
						// Draw badge overlay
						drawBadgeOnCanvas(context, callCount, color)
						resolve(canvas.toDataURL('image/png'))
					}

					image.onerror = () => {
						reject(new Error('Failed to load base favicon image'))
					}
				})
			}

			// Helper: Play audio alert (reuse primed element when URL matches)
			const playAudioAlert = soundUrl => {
				if (!soundUrl) return

				let audio
				if (
					this.audioAutoplayPrimed &&
					this._reusableAlertAudio &&
					this._primedAlertSoundUrl === soundUrl
				) {
					audio = this._reusableAlertAudio
					audio.volume = 1
					audio.currentTime = 0
				} else {
					audio = new Audio(soundUrl)
				}

				audio.play().catch(error => {
					console.warn('Audio alert autoplay blocked:', error.message)
				})
			}

			// Main logic: Handle no new calls case
			if (newCallsCount === 0) {
				restoreDefaultFavicon()
				this.stopRepeatingAudioAlert()
				return
			}

			// Handle visual alert
			if (visual_alert_enabled) {
				createBadgeCanvas(
					FAVICON_CONFIG.BASE_IMAGE_URL,
					newCallsCount,
					alert_color
				)
					.then(faviconDataUrl => {
						setFavicon(faviconDataUrl)
					})
					.catch(error => {
						console.error('Failed to create badge favicon:', error)
						// Fallback to default favicon on error
						restoreDefaultFavicon()
					})
			}

			// Handle audio alert - play every 60 seconds for 5 minutes (5 plays total)
			if (audio_alert_enabled && alert_sound) {
				playAudioAlert(alert_sound)
				if (!this.audioAlertIntervalId) {
					// Play immediately (1st play)
					this.audioAlertPlayCount = 1

					this.audioAlertIntervalId = setInterval(() => {
						// Check if we've already played 5 times
						if (this.audioAlertPlayCount > 5) {
							this.stopRepeatingAudioAlert()
							return
						}

						// Play audio if still inactive and alerts enabled
						if (
							!this.userActiveOnScreen &&
							this.alertPreferences.audio_alert_enabled
						) {
							playAudioAlert(this.alertPreferences.alert_sound)
							this.audioAlertPlayCount++
						} else {
							this.stopRepeatingAudioAlert()
						}
					}, 60 * 1000)
				}
			}
		},

		stopRepeatingAudioAlert() {
			if (this.audioAlertIntervalId) {
				clearInterval(this.audioAlertIntervalId)
				this.audioAlertIntervalId = null
				this.audioAlertPlayCount = 0
			}
		},

		/**
		 * Attempt to refresh authentication token with retry logic.
		 * Retries up to 5 times if refresh fails.
		 *
		 * @return {Promise<void>}
		 */
		async attemptRefreshToken() {
			const maxAttempts = 3
			let attempt = 0

			const retry = async () => {
				if (this.attemptRefreshCancelled) return
				try {
					attempt++

					const success = await this.refreshAuthenticationInfo({
						refresh_token: this.session.refresh_token,
					})
					if (this.attemptRefreshCancelled) return

					if (success) return

					if (attempt >= maxAttempts) {
						this.$alert.error(
							'Session expired. Please login again.'
						)
						this.logout()

						this.$router.push({ path: '/' })
						return
					}

					this.attemptRefreshIntervalId = setTimeout(retry, 60 * 1000)
				} catch (err) {
					if (this.attemptRefreshCancelled) return

					if (attempt >= maxAttempts) {
						this.$alert.error(
							'Session expired. Please login again.'
						)
						this.logout()

						this.$router.push({ path: '/' })
						return
					}

					this.attemptRefreshIntervalId = setTimeout(retry, 60 * 1000)
				}
			}

			retry() // first attempt immediately
		},

		...mapActions('calls', [
			'addToFront',
			'addToFilter',
			'updateToFilter',
			'addRecentCall',
			'setIncompleteCallCount',
			'getIncompleteCallCount',
			'get',
			'setIncompleteTotalCountOnly',
		]),
		...mapActions({
			getCallByType: 'callTypes/getByType',
			getCallTypeCounts: 'callTypes/get',
			updateCallTypeCount: 'callTypes/updateCallTypeCount',
			getAlertPopStatus: 'settings/getAlertPopStatus',
			refreshAuthenticationInfo: 'auth/refreshAuthenticationInfo',
			logout: 'auth/logout',
			getIncompletePagingCount: 'pages/getIncompletePagingCount',
			setActivePartner: 'partners/setActive',
		}),
		...mapGetters({
			startDateFilter: 'calls/startDateFilter',
			endDateFilter: 'calls/endDateFilter',
			partnerHasPagingEnabled: 'partners/hasPagingEnabled',
		}),
		...mapActions({
			incrementPendingCallsCountById:
				'partners/incrementPendingCallsCountById',
			getAlertSettings: 'settings/getAlertSettings',
		}),
		...mapMutations('calls', {
			updateUserActiveOnScreen: UPDATE_USER_ACTIVE_ON_SCREEN,
			updateCallCountDuringInactivity: UPDATE_CALL_COUNT_DURING_INACTIVITY,
		}),
	},

	/**
	 * The component's name used for debugging.
	 *
	 * @type {String}
	 */
	name: 'Manager',

	/**
	 * The component's property watchers.
	 *
	 * @type {Object}
	 */
	watch: {
		/**
		 * Watch the active partner id for changes.
		 * immediate: true — after refresh, migration may already have updated the store
		 * in router middleware while the URL still has the legacy :partner; sync on mount.
		 *
		 * @param {Number} id
		 * @return {void}
		 */
		activePartnerId: {
			handler(id) {
				const partner = parseInt(id, 10)

				if (this.shouldRedirectToPartner(partner)) {
					this.redirectToPartner(partner)
				}
			},
			immediate: true,
		},
		'$route.path'() {
			this.$nextTick(() => {
				;[
					'.call',
					'.card-history-list-container',
					'.card-notes-list-container',
				].forEach(selector => {
					const element = document.querySelector(selector)
					if (element) {
						element.scrollTo({ top: 0, behavior: 'smooth' })
					}
				})
			})
		},
		userActiveOnScreen(active) {
			if (active) {
				this.updateCallCountDuringInactivity(0)
				this.stopRepeatingAudioAlert()
			}
		},
		callCountDuringInactivity(newCallsCount) {
			// Reset the interval and play count when call count increases
			this.stopRepeatingAudioAlert()
			this.pingVisualAndAudioAlert({
				newCallsCount,
			})
		},

		'alertPreferences.audio_alert_enabled': {
			handler(enabled) {
				this.clearAudioEngagementTimer()
				if (!enabled) {
					this.localAudioEngagementPopupOpen = false
					return
				}
				this.$nextTick(() => {
					if (
						this.appUserInteractionReceived ||
						!this.alertPreferences?.audio_alert_enabled
					) {
						return
					}
					this.audioEngagementTimerId = setTimeout(() => {
						this.audioEngagementTimerId = null
						if (
							this.appUserInteractionReceived ||
							!this.alertPreferences?.audio_alert_enabled
						) {
							return
						}
						this.localAudioEngagementPopupOpen = true
					}, 10 * 1000)
				})
			},
			immediate: true,
		},

		'session.access_token': {
			handler(newValue, oldValue) {
				if (newValue && newValue !== oldValue) {
					// Clear existing timeout
					if (this.refreshTokenTimeoutId) {
						clearTimeout(this.refreshTokenTimeoutId)
						this.refreshTokenTimeoutId = null
					}

					const expiresIn = Number(this.session?.expires_in) || 900
					const refreshBefore = 180 // 3 minutes

					const cachedAt =
						this.session?.cached_at || new Date().toISOString()

					const elapsedSeconds = Math.floor(
						(Date.now() - new Date(cachedAt).getTime()) / 1000
					)

					const remainingSeconds =
						expiresIn - elapsedSeconds - refreshBefore

					const delay = Math.max(0, remainingSeconds * 1000)

					this.refreshTokenTimeoutId = setTimeout(async () => {
						await this.attemptRefreshToken()
						this.refreshTokenTimeoutId = null
					}, delay)
				}
			},
			immediate: true,
		},
		user: {
			handler(newValue, oldValue) {
				this.updateIntercom(newValue, oldValue)
			},
			immediate: true,
		},
	},

	/**
	 * The component's created lifecycle hook.
	 *
	 * @return {void}
	 */
	async created() {
		this._audioPrimeInProgress = false
		this._reusableAlertAudio = null
		this._primedAlertSoundUrl = null

		this.mouseNotMoveTimeout = null
		this.audioAlertIntervalId = null
		this.audioAlertPlayCount = 0
		this.refreshTokenTimeoutId = null
		this.attemptRefreshIntervalId = null
		this.attemptRefreshCancelled = false
		this.$app
			.on('call:new', this.onNewCall)
			.on('call:updated', this.onUpdateCall)
			.on('omd:updated', this.onOmdRelease)
			.on('partner:migrated', this.onPartnerMigrated)
		this.boundClickHandler = () => this.handleWindowActivity('click')
		this.boundVisibilityHandler = () =>
			this.handleWindowActivity('visibilitychange')
		this.boundMouseHandler = () => this.handleWindowActivity('mousemove')
		this.trackInactivity()
		this.getAlertSettings()
	},

	/**
	 * The component's before destroy lifecycle hook.
	 *
	 * @return {void}
	 */
	beforeDestroy() {
		this.$app
			.off('call:new', this.onNewCall)
			.off('call:updated', this.onUpdateCall)
			.off('omd:updated', this.onOmdRelease)
			.off('partner:migrated', this.onPartnerMigrated)
		this.trackInactivity(false)
		this.stopRepeatingAudioAlert()
		// Stop refresh token timeout
		if (this.refreshTokenTimeoutId) {
			clearTimeout(this.refreshTokenTimeoutId)
			this.refreshTokenTimeoutId = null
		}
		// Cancel attempt refresh (stops in-flight retries and prevents new ones)
		this.attemptRefreshCancelled = true
		if (this.attemptRefreshIntervalId) {
			clearTimeout(this.attemptRefreshIntervalId)
			this.attemptRefreshIntervalId = null
		}
		this.clearAudioEngagementTimer()
	},

	data() {
		return {
			localAudioEngagementPopupOpen: false,
			audioEngagementTimerId: null,
			notToContinueWithAudioAlert: false,
			audioAutoplayPrimed: false,
		}
	},
}
</script>
